
import React, { useState, useEffect } from "react";
import { Key, Infinity, Zap, Eye } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

// Données des promesses avec icônes ajoutées
const promises = [
  {
    id: 1,
    title: "Clé en main",
    description:
      "Un chef de projet dédié à chaque étape, coordonnant les prestataires et assurant le bon déroulement de l'événement le jour J",
    icon: Key
  },
  {
    id: 2,
    title: "Tout inclus",
    description:
      "Réservez tous les services en un seul endroit, avec une seule facture : hébergement, espaces de travail, restauration, activités, transport, etc.",
    icon: Infinity
  },
  {
    id: 3,
    title: "Instantané",
    description:
      "La plupart de nos partenaires fournissent des devis instantanés, vous n'avez pas à attendre pour réserver !",
    icon: Zap
  },
  {
    id: 4,
    title: "Transparent",
    description:
      "Nos tarifs sont 100% transparents, et nous garantissons que vous ne paierez jamais plus qu'en direct",
    icon: Eye
  }
];

const OurPromise = () => {
  const [currentCard, setCurrentCard] = useState(1);

  // Observer for scroll position on mobile
  useEffect(() => {
    let rafId: number;
    let ticking = false;
    
    const handleScroll = (e: Event) => {
      if (!ticking) {
        rafId = requestAnimationFrame(() => {
          const carousel = e.target as HTMLElement;
          const cardWidth = carousel.offsetWidth;
          const scrollLeft = carousel.scrollLeft;
          const newCard = Math.round(scrollLeft / cardWidth) + 1;
          if (newCard !== currentCard && newCard >= 1 && newCard <= promises.length) {
            setCurrentCard(newCard);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    const carousel = document.getElementById('promises-carousel');
    if (carousel) {
      carousel.addEventListener('scroll', handleScroll);
      return () => {
        carousel.removeEventListener('scroll', handleScroll);
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
      };
    }
  }, [currentCard]);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-normal mb-6">Notre Promesse</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Nous nous engageons à rendre votre expérience de planification d'événement fluide et agréable
          </p>
        </div>

        {/* Desktop Version */}
        <div className="hidden md:grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {promises.map((promise) => (
            <Card 
              key={promise.id} 
              className="h-full bg-white border border-gray-200 rounded-2xl shadow-sm hover:border-cta-green hover:shadow-lg transition-all duration-300"
            >
              <CardHeader className="flex flex-col items-center pt-8">
                <div className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center mb-4">
                  <promise.icon size={22} strokeWidth={1.5} className="text-gray-900" />
                </div>
              </CardHeader>
              <CardContent className="text-center px-6 pb-8">
                <CardTitle className="text-xl font-semibold mb-3">{promise.title}</CardTitle>
                <p className="text-sm text-gray-600 leading-relaxed">{promise.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Mobile Version */}
        <div className="md:hidden">
          {/* Card Indicators */}
          <div className="flex justify-center gap-4 mb-8">
            {promises.map((promise) => (
              <div 
                key={promise.id}
                className={`w-10 h-10 rounded-full flex items-center justify-center font-normal text-white transition-all duration-300 ${
                  promise.id === currentCard ? 'bg-black' : 'bg-gray-400'
                }`}
              >
                {promise.id}
              </div>
            ))}
          </div>

          {/* Scrollable Carousel */}
          <div 
            id="promises-carousel"
            className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          >
            {promises.map((promise) => (
              <div key={promise.id} className="flex-none w-full snap-center">
                <Card className="bg-white border border-gray-200 rounded-2xl shadow-sm mx-2">
                  <CardHeader className="flex flex-col items-center pt-8">
                    <div className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center mb-4">
                      <promise.icon size={22} strokeWidth={1.5} className="text-gray-900" />
                    </div>
                  </CardHeader>
                  <CardContent className="text-center px-6 pb-8">
                    <CardTitle className="text-xl font-semibold mb-3">{promise.title}</CardTitle>
                    <p className="text-sm text-gray-600 leading-relaxed">{promise.description}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurPromise;
